import React from 'react';
import {useQuery, OperationVariables, QueryHookOptions, QueryResult} from '@apollo/client';
import {DocumentNode} from 'graphql';
import isLoggedOutError from '@src/util/isLoggedOutError';
import Loader from '@src/components/Loader';
import GeneralError from '@src/components/GeneralError';

type UseGuardedQueryReturnType<TData, TVariables extends OperationVariables> = Omit<
  QueryResult<TData, TVariables>,
  'data'
> & {
  RenderComponent: React.FC;
  shouldUseRenderComponent: boolean;
  data: TData | null;
};

function useGuardedQuery<TData, TVariables extends OperationVariables>(
  query: DocumentNode,
  options: QueryHookOptions<TData, TVariables> = {}
): UseGuardedQueryReturnType<TData, TVariables> {
  const optionsWithPartialRefetch: QueryHookOptions<TData, TVariables> = {
    partialRefetch: typeof options.partialRefetch !== 'undefined' ? options.partialRefetch : true,
    ...options,
  };
  const queryResults = useQuery<TData, TVariables>(query, optionsWithPartialRefetch);
  const {error, loading} = queryResults;
  const loggedOutError = error ? isLoggedOutError(error) : false;

  let RenderComponent = () => <Loader />;
  let shouldUseRenderComponent = false;

  // this is a workaround for Apollo 3.0.x returning Object.create(null) when there's an error - we want to return null.
  // Fixed with https://github.com/apollographql/react-apollo/pull/3388
  let data: TData | null;
  if (loading || loggedOutError) {
    shouldUseRenderComponent = true;
    data = null;
  } else if (error) {
    shouldUseRenderComponent = true;
    data = null;
    RenderComponent = () => <GeneralError />;
  } else data = queryResults.data || null;

  return {
    RenderComponent,
    shouldUseRenderComponent,
    ...queryResults,
    data,
  };
}

export default useGuardedQuery;
